import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Yoga Shastra";
const SITE_URL = "https://www.yoga-shastra.com";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const CANONICAL = `${SITE_URL}/#yog-goal`;

const trimDesc = (value = "", max = 155) => {
  if (!value || value.length <= max) return value;
  return `${value.slice(0, max).replace(/\s+\S*$/, "").trim()}…`;
};

const thumb = (id) =>
  id
    ? `https://img.youtube.com/vi/${id}/maxresdefault.jpg`
    : OG_IMAGE;

export const YogGoalSeoMeta = ({
  goal,
  activeVideo,
  videos = [],
}) => {
  const pageName = activeVideo?.name
    ? `${activeVideo.name} — ${goal?.title || "Yoga"}`
    : goal?.title || "Yoga Practice";

  const title = `${pageName} | ${SITE_NAME}`;

  const description = trimDesc(
    activeVideo?.seo?.metaDescription ||
      activeVideo?.description ||
      goal?.description ||
      `Guided yoga practices for ${goal?.title || "your wellness goals"}.`
  );

  const keywords = [
    ...(goal?.keywords || []),
    ...(activeVideo?.tags || []),
    ...(activeVideo?.seo?.keywords || []),
    "yoga asana",
    "guided yoga",
    "Yoga Shastra",
  ]
    .filter(Boolean)
    .join(", ");

  const itemListSchema = videos.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `${goal.title} Yoga Practices`,
        numberOfItems: videos.length,
        itemListElement: videos.slice(0, 20).map((video, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: video.name,
          url: `https://www.youtube.com/watch?v=${video.videoId}`,
          image: thumb(video.videoId),
        })),
      }
    : null;

  const videoSchema = activeVideo
    ? {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: activeVideo.name,
        description: trimDesc(
          activeVideo.seo?.metaDescription ||
            activeVideo.description ||
            description
        ),
        thumbnailUrl: thumb(activeVideo.videoId),
        contentUrl: `https://www.youtube.com/watch?v=${activeVideo.videoId}`,
        embedUrl: `https://www.youtube.com/embed/${activeVideo.videoId}`,
      }
    : null;

  return (
    <Helmet key={`${goal?.id || "goal"}-${activeVideo?.videoId || "list"}`}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={CANONICAL} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta
        property="og:image"
        content={thumb(activeVideo?.videoId)}
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta
        name="twitter:image"
        content={thumb(activeVideo?.videoId)}
      />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: `${SITE_URL}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Yoga Goals",
              item: CANONICAL,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: goal?.title || "Yoga Practice",
              item: CANONICAL,
            },
          ],
        })}
      </script>

      {itemListSchema && (
        <script type="application/ld+json">
          {JSON.stringify(itemListSchema)}
        </script>
      )}

      {videoSchema && (
        <script type="application/ld+json">
          {JSON.stringify(videoSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default YogGoalSeoMeta;
